/**
 * Contractor role project visibility — match matrix user.contractorName
 * to construction / fiber contractor fields (ACF + Contracts overlay).
 */
import {
  CONTRACTOR_TYPE_ID,
  findMatrixUser,
  getViewerEmailFromStorage,
  getViewerRoleType,
  loadUserMatrixSettings,
} from "./userMatrixStorage";

function normalizeMatrixRoleName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function isContractorRoleType(role) {
  if (!role || typeof role !== "object") return false;
  if (String(role.id || "") === CONTRACTOR_TYPE_ID) return true;
  return normalizeMatrixRoleName(role.name) === "contractor";
}

export function viewerIsContractor(viewerEmail) {
  return isContractorRoleType(getViewerRoleType(viewerEmail));
}

export function getViewerContractorName(viewerEmail) {
  const email = String(viewerEmail || getViewerEmailFromStorage() || "")
    .trim()
    .toLowerCase();
  if (!email) return "";
  const user = findMatrixUser(loadUserMatrixSettings().users, email);
  return user?.contractorName != null ? String(user.contractorName).trim() : "";
}

function normalizeContractorMatchKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function contractorFieldLabels(raw) {
  const s = String(raw || "").trim();
  if (!s) return [];
  return s
    .split(/[,;]/)
    .map((part) =>
      String(part || "")
        .trim()
        .replace(/\s*\([\d,.]+\s*ft\)\s*$/i, "")
        .trim()
    )
    .filter(Boolean);
}

export function contractorNameMatchesField(contractorName, fieldValue) {
  const want = normalizeContractorMatchKey(contractorName);
  if (!want) return false;
  const labels = contractorFieldLabels(fieldValue);
  if (!labels.length) return false;
  if (normalizeContractorMatchKey(fieldValue) === want) return true;
  return labels.some((l) => normalizeContractorMatchKey(l) === want);
}

function constructionGroupFromRow(row) {
  const j = row?.acf_groups_json;
  if (!j || typeof j !== "object") return null;
  return (
    j.construction_details ??
    j.construction_test_2 ??
    j["Construction Details"] ??
    j.construction ??
    null
  );
}

export function extractAcfContractorNames(row) {
  const g = constructionGroupFromRow(row);
  if (!g || typeof g !== "object") {
    return { construction: "", fiber: "" };
  }
  return {
    construction: String(g.construction_contractor ?? g.contractor ?? "").trim(),
    fiber: String(g.fiber_contractor ?? g.contractor_fiber ?? "").trim(),
  };
}

function extractContractContractorNames(contractSummary) {
  const fields = contractSummary?.fields;
  if (!fields || typeof fields !== "object") {
    return { construction: "", fiber: "" };
  }
  return {
    construction: String(fields.construction_contractor ?? "").trim(),
    fiber: String(fields.fiber_contractor ?? "").trim(),
  };
}

/**
 * @param {object} row project row
 * @param {string} contractorName
 * @param {object|null} [contractSummary] from contracts batch (`byProjectId[id]`)
 */
export function projectVisibleToContractor(row, contractorName, contractSummary) {
  const want = String(contractorName || "").trim();
  if (!want) return false;
  const acf = extractAcfContractorNames(row);
  if (
    contractorNameMatchesField(want, acf.construction) ||
    contractorNameMatchesField(want, acf.fiber)
  ) {
    return true;
  }
  const fromContracts = extractContractContractorNames(contractSummary);
  if (
    contractorNameMatchesField(want, fromContracts.construction) ||
    contractorNameMatchesField(want, fromContracts.fiber)
  ) {
    return true;
  }
  const contracts = Array.isArray(contractSummary?.contracts) ? contractSummary.contracts : [];
  for (const c of contracts) {
    if (c?.appliesConstruction !== false && contractorNameMatchesField(want, c.contractor)) {
      return true;
    }
    if (c?.appliesFiber !== false && contractorNameMatchesField(want, c.fiberContractor)) {
      return true;
    }
  }
  return false;
}

/** Filter project rows for a Contractor viewer (empty contractorName → none). */
export function filterProjectsForContractorViewer(rows, viewerEmail, contractFieldsByProjectId) {
  if (!viewerIsContractor(viewerEmail)) return rows;
  const name = getViewerContractorName(viewerEmail);
  if (!name) return [];
  const byId = contractFieldsByProjectId && typeof contractFieldsByProjectId === "object"
    ? contractFieldsByProjectId
    : {};
  return (Array.isArray(rows) ? rows : []).filter((row) =>
    projectVisibleToContractor(row, name, byId[row?.id] ?? byId[String(row?.id)] ?? null)
  );
}
